import { useState } from "react";
import { toast } from "sonner";
import { Mail, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We typically reply within two working days.");
      setForm({ name: "", email: "", organization: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="border-b border-[var(--eai-border)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <p className="overline">Contact</p>
          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.98] max-w-4xl">
            Tell us what you&rsquo;re working on. We read every message.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[var(--eai-ink-2)] leading-relaxed">
            For advisory engagements, press, partnerships, or editorial enquiries — use the form below. For roundtable invitations, subscribe to the briefing.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="overline">Direct line</p>
            <div className="mt-6">
              <p className="font-mono-eai text-xs uppercase tracking-[0.18em] text-[var(--eai-ink-2)]">All enquiries</p>
              <a
                href="mailto:ethicalainow@gmail.com"
                className="mt-1 block font-display text-2xl lg:text-3xl tracking-tight hover:text-[var(--eai-accent)] transition-colors duration-200 break-all"
                data-testid="contact-email-link"
              >
                ethicalainow@gmail.com
              </a>
              <p className="mt-4 text-sm text-[var(--eai-ink-2)] leading-relaxed">
                Editorial, advisory, press, and partnership enquiries — all routed through one address.
              </p>
            </div>
            <div className="mt-12 border-t border-[var(--eai-border)] pt-6 text-sm text-[var(--eai-ink-2)] leading-relaxed">
              We treat correspondence in confidence by default. For confidential disclosure of AI incidents, request a secure channel in the subject line.
            </div>
          </div>
          <div className="lg:col-span-8">
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="contact-form">
              <input required placeholder="Full name" className="eai-input sm:col-span-1" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="contact-name" />
              <input required type="email" placeholder="Email" className="eai-input sm:col-span-1" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} data-testid="contact-email" />
              <input placeholder="Organisation (optional)" className="eai-input sm:col-span-2" value={form.organization} onChange={(e)=>setForm({...form, organization: e.target.value})} data-testid="contact-org" />
              <input required placeholder="Subject" className="eai-input sm:col-span-2" value={form.subject} onChange={(e)=>setForm({...form, subject: e.target.value})} data-testid="contact-subject" />
              <textarea required placeholder="How can we help?" className="eai-input eai-textarea sm:col-span-2" value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})} data-testid="contact-message" />
              <button type="submit" disabled={busy} className="eai-btn sm:col-span-2 w-fit" data-testid="contact-submit">
                <Mail size={16} /> {busy ? "Sending…" : "Send message"} <ArrowRight size={16} />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
